import { enableMapSet } from "immer";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";
import type { BundleGroupResponse, EmailAttachmentInfoResponse } from "@/hooks/useBundle";
import type { Document } from "@/hooks/useDocuments";
import { cleanFileName, getFileExtension, tempId } from "@/utils/file-utils";
import {
  type BundleConfig,
  type BundleMetadata,
  buildDuplicateMap,
  countGroupsAndFiles,
  type DuplicateMap,
  type FileNode,
  fromGroups,
  type GroupNode,
  GroupType,
  isAutoVolumeName,
  nextVolumeName,
  insertIneligibleIds,
  normalizeInsert,
  toGroups,
  type VolumeNaming,
} from "./bundleTypes";

enableMapSet();

const EMAIL_EXTENSIONS = new Set(['.eml', '.msg']);

const isEmailDocument = (doc: Document): boolean => {
  const ext = getFileExtension(doc.fileName)?.toLowerCase();
  return ext ? EMAIL_EXTENSIONS.has(ext) : false;
};

export type EditorMeta = {
  title: string;
  description: string;
  metadata: BundleMetadata | null;
};

const EMPTY_EDITOR_META: EditorMeta = {
  title: '',
  description: '',
  metadata: null,
};

type InitializeArgs = {
  matterGuid: string;
  initialGroups: BundleGroupResponse[];
  initialEmailAttachments?: Record<string, EmailAttachmentInfoResponse[]>;
  title: string;
  description: string | null;
  metadata: BundleMetadata;
};

export type BundleTreeActions = {
  /** Replace the entire store state with fresh data — called when a bundle loads or reloads. */
  initialize: (args: InitializeArgs) => void;

  toggleExpand: (id: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  expandGroup: (id: string) => void;

  updateFile: (fileId: string, updates: Partial<FileNode>) => void;
  removeFile: (fileId: string) => void;
  updateGroup: (groupId: string, updates: Partial<GroupNode>) => void;
  removeGroup: (groupId: string) => void;
  addGroup: (
    groupId: string,
    title: string,
    parentGroupId: string | null,
    groupType: GroupType,
    extras?: Partial<GroupNode>,
  ) => void;
  addFiles: (groupId: string, newFiles: FileNode[]) => void;
  moveChild: (childId: string, targetParentId: string | null, beforeId?: string) => void;

  setAttachmentIncluded: (fileId: string, included: boolean) => void;

  addDocuments: (docs: Document[]) => string;
  renameAutoVolumes: (naming: VolumeNaming) => void;

  setEditorTitle: (title: string) => void;
  setEditorDescription: (description: string) => void;
  setEditorMetadata: (metadata: BundleMetadata) => void;
  setTitlePagePending: (pending: boolean) => void;

  /** Capture current full fingerprint into previewedFingerprint — call after a successful preview generation. */
  markPreviewed: () => void;
};

export type BundleTreeState = {
  matterGuid: string;
  config: BundleConfig;
  expandedIds: Set<string>;
  editorMeta: EditorMeta;
  savedFingerprint: string;
  previewedFingerprint: string | null;
  titlePagePending: boolean;
  actions: BundleTreeActions;
};

const EMPTY_CONFIG: BundleConfig = {
  rootChildIds: [],
  groups: {},
  files: {},
};

// Cache fullFingerprint per (config, editorMeta) pair. Both keys are objects produced
// fresh by Immer on each mutation, so a nested WeakMap is safe and self-evicting.
// Declared before the store creator because `create` invokes the initial-state factory
// synchronously and that factory calls `computeFullFingerprint`.
const fullFingerprintCache = new WeakMap<BundleConfig, WeakMap<EditorMeta, string>>();
function computeFullFingerprint(config: BundleConfig, editorMeta: EditorMeta): string {
  let inner = fullFingerprintCache.get(config);
  if (!inner) {
    inner = new WeakMap();
    fullFingerprintCache.set(config, inner);
  }
  const cached = inner.get(editorMeta);
  if (cached !== undefined) return cached;
  const f = JSON.stringify({
    title: editorMeta.title.trim(),
    description: editorMeta.description.trim() || null,
    metadata: editorMeta.metadata,
    groups: toGroups(config),
  });
  inner.set(editorMeta, f);
  return f;
}

/**
 * Module-level singleton store. One bundle editor is mounted at a time (edit page or view page),
 * so a scoped store per-instance isn't needed — `initialize` resets the state when a new bundle
 * loads. Use `useBundleTreeStore(selector)` for subscriptions, `useBundleTreeStore.getState()`
 * for imperative reads, and `useBundleTreeStore.subscribe(...)` for external listeners.
 */
export const useBundleTreeStore = create<BundleTreeState>()(
  immer((set) => ({
    matterGuid: '',
    config: EMPTY_CONFIG,
    expandedIds: new Set<string>(),
    editorMeta: EMPTY_EDITOR_META,
    savedFingerprint: computeFullFingerprint(EMPTY_CONFIG, EMPTY_EDITOR_META),
    previewedFingerprint: null,
    titlePagePending: false,
    actions: {
      initialize: (args) => set((draft) => {
        const initialConfig = fromGroups(args.initialGroups, args.initialEmailAttachments);
        const initialMeta: EditorMeta = {
          title: args.title,
          description: args.description ?? '',
          metadata: args.metadata,
        };
        draft.matterGuid = args.matterGuid;
        draft.config = initialConfig;
        draft.expandedIds = new Set(Object.keys(initialConfig.groups));
        draft.editorMeta = initialMeta;
        draft.savedFingerprint = computeFullFingerprint(initialConfig, initialMeta);
        draft.previewedFingerprint = null;
        draft.titlePagePending = false;
      }),

      toggleExpand: (id) => set((draft) => {
        if (draft.expandedIds.has(id)) draft.expandedIds.delete(id);
        else draft.expandedIds.add(id);
      }),

      expandAll: () => set((draft) => {
        draft.expandedIds = new Set(Object.keys(draft.config.groups));
      }),

      collapseAll: () => set((draft) => {
        draft.expandedIds = new Set();
      }),

      expandGroup: (id) => set((draft) => {
        draft.expandedIds.add(id);
      }),

      updateFile: (fileId, updates) => set((draft) => {
        const f = draft.config.files[fileId];
        if (f) Object.assign(f, updates);
        if (updates.isInsert) normalizeInsert(draft.config);
      }),

      removeFile: (fileId) => set((draft) => {
        const f = draft.config.files[fileId];
        if (!f) return;
        removeFromParentDraft(draft.config, fileId, f.parentGroupId);
        delete draft.config.files[fileId];
        normalizeInsert(draft.config);
      }),

      updateGroup: (groupId, updates) => set((draft) => {
        const g = draft.config.groups[groupId];
        if (g) Object.assign(g, updates);
        normalizeInsert(draft.config);
      }),

      removeGroup: (groupId) => set((draft) => {
        const group = draft.config.groups[groupId];
        if (!group) return;

        const allGroupIds = [groupId, ...collectDescendantGroupIds(draft.config, groupId)];
        const fileIdsToRemove: string[] = [];
        for (const gid of allGroupIds) {
          for (const cid of draft.config.groups[gid]?.children || []) {
            if (draft.config.files[cid]) fileIdsToRemove.push(cid);
          }
        }

        removeFromParentDraft(draft.config, groupId, group.parentGroupId);
        for (const gid of allGroupIds) {
          delete draft.config.groups[gid];
        }
        for (const fid of fileIdsToRemove) {
          delete draft.config.files[fid];
        }
        normalizeInsert(draft.config);
      }),

      addGroup: (groupId, title, parentGroupId, groupType, extras) => set((draft) => {
        draft.config.groups[groupId] = { groupId, title, parentGroupId, groupType, isNew: true, children: [], ...extras };
        if (parentGroupId === null) {
          draft.config.rootChildIds.push(groupId);
        } else {
          const parent = draft.config.groups[parentGroupId];
          if (parent) parent.children.push(groupId);
        }
        draft.expandedIds.add(groupId);
        normalizeInsert(draft.config);
      }),

      addFiles: (groupId, newFiles) => set((draft) => {
        const parent = draft.config.groups[groupId];
        if (!parent) return;
        for (const f of newFiles) {
          draft.config.files[f.fileId] = f;
          parent.children.push(f.fileId);
        }
        normalizeInsert(draft.config);
      }),

      moveChild: (childId, targetParentId, beforeId) => set((draft) => {
        const config = draft.config;
        const currentParent =
          config.files[childId]?.parentGroupId ??
          config.groups[childId]?.parentGroupId ??
          null;

        const srcArr = currentParent === null ? config.rootChildIds : (config.groups[currentParent]?.children || []);
        const srcIdx = srcArr.indexOf(childId);
        if (srcIdx >= 0) srcArr.splice(srcIdx, 1);

        const dstArr = targetParentId === null ? config.rootChildIds : (config.groups[targetParentId]?.children ?? []);
        const insertIdx = beforeId ? dstArr.indexOf(beforeId) : -1;
        dstArr.splice(insertIdx === -1 ? dstArr.length : insertIdx, 0, childId);

        if (config.files[childId]) {
          config.files[childId].parentGroupId = targetParentId;
        } else if (config.groups[childId]) {
          config.groups[childId].parentGroupId = targetParentId;
        }
        normalizeInsert(config);
      }),

      setAttachmentIncluded: (fileId, included) => set((draft) => {
        const f = draft.config.files[fileId];
        if (!f) return;
        f.includedInBundle = included ? undefined : false;
        // When re-including, move the file to the end of its email group's children so it
        // renders at the bottom of the "included" list rather than wherever the metadata
        // order had placed it among the unincluded ones.
        if (included && f.parentGroupId) {
          const parent = draft.config.groups[f.parentGroupId];
          if (parent?.groupType === GroupType.EMAIL) {
            const idx = parent.children.indexOf(fileId);
            if (idx !== -1) {
              parent.children.splice(idx, 1);
              parent.children.push(fileId);
            }
          }
        }
        normalizeInsert(draft.config);
      }),

      addDocuments: (docs) => {
        let resolvedTargetId = '';
        set((draft) => {
          const config = draft.config;
          for (const doc of docs) {
            if (isEmailDocument(doc)) {
              const emailGroupId = tempId();
              if (!resolvedTargetId) resolvedTargetId = emailGroupId;
              const emailFileId = tempId();
              const attachmentEntries = (doc.attachments || []).map((a) => {
                const attId = tempId();
                config.files[attId] = makeFileNode(attId, a.id, a.fileName, emailGroupId);
                return attId;
              });
              config.files[emailFileId] = makeFileNode(emailFileId, doc.id, doc.fileName, emailGroupId);
              config.groups[emailGroupId] = {
                groupId: emailGroupId,
                title: doc.subject?.trim() || cleanFileName(doc.fileName),
                parentGroupId: null,
                groupType: GroupType.EMAIL,
                isNew: true,
                children: [emailFileId, ...attachmentEntries],
              };
              config.rootChildIds.push(emailGroupId);
              draft.expandedIds.add(emailGroupId);
            } else {
              const fileId = tempId();
              if (!resolvedTargetId) resolvedTargetId = fileId;
              config.files[fileId] = makeFileNode(fileId, doc.id, doc.fileName, null);
              config.rootChildIds.push(fileId);
            }
          }
          normalizeInsert(config);
        });
        return resolvedTargetId;
      },

      renameAutoVolumes: (naming) => set((draft) => {
        const config = draft.config;
        const volumeIds = config.rootChildIds.filter((id) => config.groups[id]?.groupType === GroupType.VOLUME);
        const customNames = volumeIds
          .filter((id) => !isAutoVolumeName(config.groups[id].title))
          .map((id) => config.groups[id].title);
        const takenNames = [...customNames];
        for (const vid of volumeIds) {
          const vol = config.groups[vid];
          if (vol && isAutoVolumeName(vol.title)) {
            const name = nextVolumeName(naming, takenNames);
            vol.title = name;
            takenNames.push(name);
          }
        }
      }),

      setEditorTitle: (title) => set((draft) => {
        draft.editorMeta.title = title;
      }),

      setEditorDescription: (description) => set((draft) => {
        draft.editorMeta.description = description;
      }),

      setEditorMetadata: (metadata) => set((draft) => {
        draft.editorMeta.metadata = metadata;
      }),

      setTitlePagePending: (pending) => set((draft) => {
        draft.titlePagePending = pending;
      }),

      markPreviewed: () => set((draft) => {
        draft.previewedFingerprint = computeFullFingerprint(draft.config, draft.editorMeta);
      }),
    },
  })),
);

// ── Thin selector-style wrappers for call sites that find them more readable ──

export function useBundleTree<T>(selector: (s: BundleTreeState) => T): T {
  return useBundleTreeStore(selector);
}

export function useBundleTreeActions(): BundleTreeActions {
  return useBundleTreeStore((s) => s.actions);
}

export function useEditorMeta(): EditorMeta {
  return useBundleTreeStore((s) => s.editorMeta);
}

// ── Derived predicates / counts ──

/**
 * Stable JSON fingerprint of the full editor state — title, description, metadata, and tree —
 * matching the exact `updateBundle` payload shape so any change that round-trips to the server
 * is reflected. UI-only state (expand/collapse, scroll, drag hover) is excluded.
 *
 * The title-page binary buffer is intentionally NOT in this fingerprint; it's tracked
 * separately as a `titlePagePending` boolean and OR'd into `isDirtyOf`.
 */
function fullFingerprintOf(state: BundleTreeState): string {
  return computeFullFingerprint(state.config, state.editorMeta);
}

function isDirtyOf(state: BundleTreeState): boolean {
  if (state.titlePagePending) return true;
  return fullFingerprintOf(state) !== state.savedFingerprint;
}

function isPreviewOutdatedOf(state: BundleTreeState): boolean {
  if (state.previewedFingerprint === null) return false;
  return fullFingerprintOf(state) !== state.previewedFingerprint;
}

export function useIsDirty(): boolean {
  return useBundleTreeStore(isDirtyOf);
}

export function useIsPreviewOutdated(): boolean {
  return useBundleTreeStore(isPreviewOutdatedOf);
}

// ── React selector hooks for derived values ──
//
// All derived values are cached per config reference so every consumer reads the same
// object and Zustand's reference equality prevents cross-consumer re-renders. Caches are
// keyed on the Immer-produced config (or files) ref — a new ref on any mutation, otherwise
// stable, which is exactly the invalidation signal we want.

const duplicateMapCache = new WeakMap<BundleConfig, DuplicateMap>();
function duplicateMapOf(config: BundleConfig): DuplicateMap {
  let m = duplicateMapCache.get(config);
  if (!m) {
    m = buildDuplicateMap(config);
    duplicateMapCache.set(config, m);
  }
  return m;
}

export function useDuplicateMap(): DuplicateMap {
  return useBundleTreeStore((s) => duplicateMapOf(s.config));
}

const allFileGuidsCache = new WeakMap<Record<string, FileNode>, string[]>();
function allFileGuidsOf(files: Record<string, FileNode>): string[] {
  let g = allFileGuidsCache.get(files);
  if (!g) {
    g = Object.values(files)
      .filter((f) => f.includedInBundle !== false)
      .map((f) => f.fileReferenceGuid);
    allFileGuidsCache.set(files, g);
  }
  return g;
}

export function useAllFileGuids(): string[] {
  return useBundleTreeStore((s) => allFileGuidsOf(s.config.files));
}

const insertIneligibleCache = new WeakMap<BundleConfig, Set<string>>();
function insertIneligibleOf(config: BundleConfig): Set<string> {
  let s = insertIneligibleCache.get(config);
  if (!s) {
    s = insertIneligibleIds(config);
    insertIneligibleCache.set(config, s);
  }
  return s;
}

/** File IDs that cannot be made inserts. */
export function useInsertIneligibleIds(): Set<string> {
  return useBundleTreeStore((s) => insertIneligibleOf(s.config));
}

const countsCache = new WeakMap<BundleConfig, ReturnType<typeof countGroupsAndFiles>>();
function countsOf(config: BundleConfig) {
  let c = countsCache.get(config);
  if (!c) {
    c = countGroupsAndFiles(config);
    countsCache.set(config, c);
  }
  return c;
}

export function useBundleCounts() {
  return useBundleTreeStore(useShallow((s) => countsOf(s.config)));
}

const volumesCache = new WeakMap<BundleConfig, { index: number; label: string }[]>();
function volumesOf(config: BundleConfig): { index: number; label: string }[] {
  let v = volumesCache.get(config);
  if (!v) {
    v = config.rootChildIds
      .filter((id) => config.groups[id]?.groupType === GroupType.VOLUME)
      .map((id, index) => ({ index, label: config.groups[id]?.title || `Volume ${index + 1}` }));
    volumesCache.set(config, v);
  }
  return v;
}

/** Top-level volumes in root order with their 0-based index and title, for per-volume preview. */
export function useBundleVolumes(): { index: number; label: string }[] {
  return useBundleTreeStore((s) => volumesOf(s.config));
}

/** True when the tree contains something meaningfully expandable: a SECTION with any child,
 *  or an EMAIL group with at least one included attachment. Empty sections, volumes, root-level
 *  files, and emails with no included attachments all contribute nothing to expand/collapse,
 *  so the global expand/collapse button should be hidden for them. Used in both Edit and View
 *  tree shells. */
export function useHasExpandable(): boolean {
  return useBundleTreeStore((s) => {
    for (const g of Object.values(s.config.groups)) {
      if (g.groupType === GroupType.SECTION) {
        if ((g.children?.length ?? 0) > 0) return true;
      } else if (g.groupType === GroupType.EMAIL) {
        const children = g.children ?? [];
        // children[0] is the email file; children[1..] are attachments.
        for (let i = 1; i < children.length; i++) {
          const file = s.config.files[children[i]];
          if (file && file.includedInBundle !== false) return true;
        }
      }
    }
    return false;
  });
}

// ── Draft mutation helpers (all operate on the mutable draft) ──

function makeFileNode(fileId: string, fileReferenceGuid: string, fileName: string, parentGroupId: string | null): FileNode {
  return {
    fileId,
    fileReferenceGuid,
    fileName: cleanFileName(fileName),
    fileExtension: getFileExtension(fileName),
    customName: null,
    pageRange: null,
    isInsert: false,
    parentGroupId,
  };
}

function removeFromParentDraft(config: BundleConfig, childId: string, parentGroupId: string | null) {
  const arr = parentGroupId === null ? config.rootChildIds : config.groups[parentGroupId]?.children;
  if (!arr) return;
  const i = arr.indexOf(childId);
  if (i >= 0) arr.splice(i, 1);
}

function collectDescendantGroupIds(config: BundleConfig, groupId: string): string[] {
  const out: string[] = [];
  for (const cid of config.groups[groupId]?.children || []) {
    if (config.groups[cid]) {
      out.push(cid);
      out.push(...collectDescendantGroupIds(config, cid));
    }
  }
  return out;
}
